'use client'

import { useAuth } from '@/components/AuthProvider'
import { usePathname } from '@/hooks/usePathname'
import { useTranslations } from '@/hooks/useTranslations'
import { isNative } from '@/lib/platform'
import { STORE_URLS } from '@/lib/store-links'
import { resolveAppBanner } from '@/lib/app-banner'
import { trackEvent } from '@/lib/posthog'

// "Get the app" nudge for logged-in users browsing the SPA on a phone/tablet.
// Stays dark until the store URLs in store-links.ts are filled in at launch,
// and never renders for contractors or on public routes (visibility rules in
// app-banner.ts). Deliberately NOT dismissible — the mobile browser version
// is unsupported, so the banner stays until the user switches to the app.
// The store link is an external full-document navigation away from the SPA —
// web-only by construction, so the hard-nav.ts rule (native PowerSync
// teardown) doesn't apply here.
export function SmartAppBanner() {
  const { user, userRole } = useAuth()
  const pathname = usePathname()
  const t = useTranslations('components.appBanner')

  if (!user) return null

  const target = resolveAppBanner({
    isNativePlatform: isNative(),
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    userRole,
    pathname,
    urls: STORE_URLS,
  })
  if (!target) return null

  return (
    // No breakpoint gating — the OS detection already scopes this to iOS/
    // Android browsers, and an iPad in landscape is wider than lg.
    // Rendered INSIDE the sticky .paper-nav element (bottom of Navbar.tsx),
    // directly under the nav bar — so it's pinned on every page and stays
    // visible while scrolling.
    <div className="flex items-center gap-3 border-[1.5px] border-gray-400 bg-gray-50 px-4 py-2.5">
      <p className="min-w-0 flex-1 text-sm text-gray-700">{t('message')}</p>
      {/* NB: paper-button-sm is a full (grey) button style, not a size
          modifier — combining it would override the color. Variant class alone.
          paper-button-primary = the navy of the dashboard "Neue Analyse" CTA. */}
      <a
        href={target.url}
        className="paper-button-primary shrink-0 whitespace-nowrap"
        onClick={() => trackEvent('app_banner_clicked', { os: target.os })}
      >
        {t('cta')}
      </a>
    </div>
  )
}
